/**
 * team members actions
 */

import {
  TEAM_MEMBERS_CHANGE,
  TEAM_MEMBERS_EDIT_CHANGE,
  TEAM_MEMBERS_ADD,
  TEAM_MEMBERS_DELETE,
  TEAM_MEMBERS_GET,
  TEAM_MEMBERS_GET_ALL,
  TEAM_MEMBERS_RESET,
  SET_TEAM_MEMBERS_LOADING,
  SET_TEAM_MEMBERS_FORM_ERRORS,
  SET_TEAM_MEMBERS_FORM_EDIT_ERRORS,
} from './constants';
import axios from 'axios';
import { API_URL } from '../../constants';
import { showNotification } from '../Notification/actions';
import handleError from '../../utils/error';
import { allFieldsValidation } from '../../utils/validation';

const getImageFile = (image) => {
  if (!image) return null;
  if (Array.isArray(image)) return image[0] || null;
  if (image instanceof File) return image;
  return null;
};

export const teamMemberChange = (name, value) => {
  return {
    type: TEAM_MEMBERS_CHANGE,
    payload: { [name]: value },
  };
};

export const teamMemberEditChange = (name, value) => {
  return {
    type: TEAM_MEMBERS_EDIT_CHANGE,
    payload: { [name]: value },
  };
};

export const resetTeamMember = () => {
  return async (dispatch) => {
    dispatch({ type: TEAM_MEMBERS_RESET });
  };
};

const setTeamMembersLoading = (loading) => {
  return {
    type: SET_TEAM_MEMBERS_LOADING,
    payload: loading,
  };
};

export const fetchTeamMember = (id) => {
  return async (dispatch) => {
    dispatch(setTeamMembersLoading(true));
    try {
      const response = await axios.get(`${API_URL}/team-members/${id}`);
      if (response.status === 200 && response.data.teamMember) {
        dispatch({ type: TEAM_MEMBERS_GET, payload: response.data.teamMember });
      }
    } catch (error) {
      dispatch(showNotification('error', 'Failed to fetch team member'));
      handleError(error, dispatch);
    } finally {
      dispatch(setTeamMembersLoading(false));
    }
  };
};

export const fetchTeamMembers = () => {
  return async (dispatch) => {
    dispatch(setTeamMembersLoading(true));
    try {
      const response = await axios.get(`${API_URL}/team-members`);
      if (response.status === 200 && response.data.teamMembers) {
        dispatch({ type: TEAM_MEMBERS_GET_ALL, payload: response.data.teamMembers });
      }
    } catch (error) {
      dispatch(showNotification('error', 'Failed to fetch team members'));
      handleError(error, dispatch);
    } finally {
      dispatch(setTeamMembersLoading(false));
    }
  };
};

export const addTeamMember = (navigate) => {
  return async (dispatch, getState) => {
    dispatch(setTeamMembersLoading(true));
    try {
      const rules = {
        name: 'required',
        role: 'required',
        bio: 'required',
        image: 'required',
      };

      const teamMember = getState().teamMembers.teamMemberFormData;
      const imageFile = getImageFile(teamMember.image);

      const newTeamMember = {
        name: teamMember.name,
        role: teamMember.role,
        bio: teamMember.bio,
        image: imageFile,
      };

      const { isValid, errors } = allFieldsValidation(newTeamMember, rules, {
        'required.name': 'Name is required.',
        'required.role': 'Role is required.',
        'required.bio': 'Bio is required.',
        'required.image': 'Image is required.',
      });

      if (!isValid) {
        dispatch({ type: SET_TEAM_MEMBERS_FORM_ERRORS, payload: errors });
        return;
      }

      const formData = new FormData();
      formData.set('name', newTeamMember.name);
      formData.set('role', newTeamMember.role);
      formData.set('bio', newTeamMember.bio);
      formData.append('image', imageFile);

      const response = await axios.post(`${API_URL}/team-members/add`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.data.success === true) {
        dispatch(showNotification('success', response.data.message));
        dispatch({ type: TEAM_MEMBERS_ADD, payload: response.data.teamMember });
        dispatch(resetTeamMember());
        navigate(-1);
      }
    } catch (error) {
      handleError(error, dispatch, 'Error saving team member, try again!');
    } finally {
      dispatch(setTeamMembersLoading(false));
    }
  };
};

export const updateTeamMember = (navigate) => {
  return async (dispatch, getState) => {
    dispatch(setTeamMembersLoading(true));
    try {
      const rules = {
        name: 'required',
        role: 'required',
        bio: 'required',
      };

      const teamMember = getState().teamMembers.teamMember;
      const imageFile = getImageFile(teamMember.image);

      const updatedTeamMember = {
        name: teamMember.name,
        role: teamMember.role,
        bio: teamMember.bio,
      };

      const { isValid, errors } = allFieldsValidation(updatedTeamMember, rules, {
        'required.name': 'Name is required.',
        'required.role': 'Role is required.',
        'required.bio': 'Bio is required.',
      });

      if (!isValid) {
        dispatch({ type: SET_TEAM_MEMBERS_FORM_EDIT_ERRORS, payload: errors });
        return;
      }

      const formData = new FormData();
      formData.set('name', updatedTeamMember.name);
      formData.set('role', updatedTeamMember.role);
      formData.set('bio', updatedTeamMember.bio);
      if (imageFile) {
        formData.append('image', imageFile);
      }

      const response = await axios.put(`${API_URL}/team-members/${teamMember._id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.data.success === true) {
        dispatch(showNotification('success', response.data.message));
        dispatch(resetTeamMember());
        navigate(-1);
      }
    } catch (error) {
      handleError(error, dispatch, 'Error updating team member, try again!');
    } finally {
      dispatch(setTeamMembersLoading(false));
    }
  };
};

export const deleteTeamMember = (id, navigate) => {
  return async (dispatch) => {
    try {
      const response = await axios.delete(`${API_URL}/team-members/${id}`);

      if (response.data.success === true) {
        dispatch(showNotification('success', response.data.message));
        dispatch({ type: TEAM_MEMBERS_DELETE, payload: id });
        navigate(-1);
      }
    } catch (error) {
      handleError(error, dispatch, 'Error deleting team member, try again!');
    }
  };
};
